import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Zap, Plus, Trash2, Play, Calendar, UserPlus, FolderPlus, FileStack, RotateCw, ArrowLeft, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../fetchInterceptor';
import InfoTooltip from '../components/InfoTooltip';
import { useWorkspace } from '../context/WorkspaceContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRIGGER_META = {
  on_contact_create: { icon: UserPlus, label: 'When a new contact is added', color: 'text-emerald-500' },
  monthly: { icon: Calendar, label: 'Monthly (on a chosen day)', color: 'text-blue-500' },
  yearly: { icon: Calendar, label: 'Yearly (on a chosen month + day)', color: 'text-amber-500' },
  manual: { icon: Play, label: 'Manual only (run on demand)', color: 'text-slate-500' },
};

const ACTION_META = {
  apply_template: { icon: FileStack, label: 'Apply a folder template' },
  create_folder: { icon: FolderPlus, label: 'Create a single folder' },
  rollover: { icon: RotateCw, label: 'Roll forward to next year' },
};

const emptyDraft = {
  name: '',
  description: '',
  trigger_type: 'on_contact_create',
  trigger_config: {},
  action_type: 'apply_template',
  template_id: '',
  target_space: 'firm',
  target_parent_folder_id: '',
  folder_name: '',
  rollover_source_folder_id: '',
  enabled: true,
};

export default function FolderAutomations() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [folders, setFolders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        apiFetch(`${API_URL}/api/workspaces/${wsId}/folder-automations`),
        apiFetch(`${API_URL}/api/folder-templates?workspace_id=${wsId}`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/folders`),
      ]);
      if (r1.ok) setRows(await r1.json());
      if (r2.ok) setTemplates(await r2.json());
      if (r3.ok) setFolders(await r3.json());
    } finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setDraft(emptyDraft); setEditingId(null); setShowForm(true); };
  const openEdit = (row) => {
    setDraft({
      name: row.name,
      description: row.description || '',
      trigger_type: row.trigger_type,
      trigger_config: row.trigger_config || {},
      action_type: row.action_type,
      template_id: row.template_id || '',
      target_space: row.target_space || 'firm',
      target_parent_folder_id: row.target_parent_folder_id || '',
      folder_name: row.folder_name || '',
      rollover_source_folder_id: row.rollover_source_folder_id || '',
      enabled: row.enabled,
    });
    setEditingId(row.automation_id);
    setShowForm(true);
  };

  const save = async () => {
    if (!draft.name.trim()) return toast.error('Name is required');
    if (draft.action_type === 'apply_template' && !draft.template_id) return toast.error('Pick a template');
    if (draft.action_type === 'create_folder' && !draft.folder_name.trim()) return toast.error('Folder name is required');
    if (draft.action_type === 'rollover' && !draft.rollover_source_folder_id) return toast.error('Pick a source folder to roll over');

    const body = { ...draft, workspace_id: wsId };
    // Strip empty optional strings to null
    ['template_id', 'target_parent_folder_id', 'folder_name', 'rollover_source_folder_id'].forEach(k => {
      if (!body[k]) body[k] = null;
    });

    let r;
    if (editingId) {
      r = await apiFetch(`${API_URL}/api/folder-automations/${editingId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
    } else {
      r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/folder-automations`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
    }
    if (r.ok) {
      toast.success(editingId ? 'Automation updated' : 'Automation created');
      setShowForm(false);
      fetchAll();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this automation? This cannot be undone.')) return;
    const r = await apiFetch(`${API_URL}/api/folder-automations/${id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Automation deleted'); fetchAll(); }
    else toast.error('Delete failed');
  };

  const toggle = async (row) => {
    const r = await apiFetch(`${API_URL}/api/folder-automations/${row.automation_id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: !row.enabled }),
    });
    if (r.ok) fetchAll();
  };

  const runNow = async (id) => {
    const r = await apiFetch(`${API_URL}/api/folder-automations/${id}/run`, { method: 'POST' });
    if (r.ok) {
      const data = await r.json();
      const ok = data.result?.ok;
      toast[ok ? 'success' : 'error'](data.result?.detail || (ok ? 'Ran' : 'Failed'));
      fetchAll();
    } else { toast.error('Run failed'); }
  };

  const runDue = async () => {
    const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/folder-automations/run-due`, { method: 'POST' });
    if (r.ok) {
      const data = await r.json();
      toast.success(`Ran ${data.ran} due automation${data.ran === 1 ? '' : 's'}`);
      fetchAll();
    }
  };

  const TriggerIcon = ({ t }) => {
    const M = TRIGGER_META[t]; if (!M) return null;
    const I = M.icon;
    return <I className={`w-4 h-4 ${M.color}`} />;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/documents')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Documents
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-500" />
            <h1 className="text-2xl font-semibold">Folder Automations</h1>
            <InfoTooltip
              content={
                <div className="space-y-1 text-xs">
                  <p><strong>What is this?</strong> Set-it-and-forget-it rules that automatically generate folders on a schedule or when something happens.</p>
                  <p><strong>Examples:</strong></p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>"When a new <em>client</em> is added → apply the Tax Engagement template"</li>
                    <li>"On the 1st of every month → create a folder named "{'{Year}-{Month}'} Reconciliation"</li>
                    <li>"On January 1st → roll my Tax Engagement folder forward to next year"</li>
                  </ul>
                </div>
              }
            />
          </div>
          <p className="text-sm text-muted-foreground mt-1">Auto-generate folders on triggers (new contact, monthly, yearly).</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={runDue} data-testid="run-due-btn">
            <FlaskConical className="w-4 h-4 mr-1" /> Run due now
          </Button>
          <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="new-automation-btn">
            <Plus className="w-4 h-4 mr-1" /> New automation
          </Button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <Card data-testid="empty-state">
          <CardContent className="p-10 text-center space-y-2">
            <Zap className="w-10 h-10 mx-auto text-amber-300" />
            <p className="font-medium">No automations yet</p>
            <p className="text-sm text-muted-foreground">Create your first rule to stop hand-creating folders every month.</p>
            <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-white mt-2">
              <Plus className="w-4 h-4 mr-1" /> Create automation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="automations-list">
          {rows.map(row => (
            <Card key={row.automation_id} className={row.enabled ? '' : 'opacity-60'} data-testid={`automation-card-${row.automation_id}`}>
              <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                <TriggerIcon t={row.trigger_type} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{row.name}</span>
                    <Badge variant="outline" className="text-[10px]">{TRIGGER_META[row.trigger_type]?.label || row.trigger_type}</Badge>
                    <Badge variant="secondary" className="text-[10px]">{ACTION_META[row.action_type]?.label || row.action_type}</Badge>
                    {row.next_run_at && row.trigger_type !== 'on_contact_create' && row.trigger_type !== 'manual' && (
                      <Badge variant="outline" className="text-[10px]">Next: {new Date(row.next_run_at).toLocaleDateString()}</Badge>
                    )}
                    <Badge variant="outline" className="text-[10px]">Runs: {row.run_count || 0}</Badge>
                  </div>
                  {row.description && <p className="text-xs text-muted-foreground mt-0.5">{row.description}</p>}
                </div>
                <Switch checked={row.enabled} onCheckedChange={() => toggle(row)} data-testid={`toggle-${row.automation_id}`} />
                <Button size="sm" variant="ghost" onClick={() => runNow(row.automation_id)} data-testid={`run-${row.automation_id}`}>
                  <Play className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => openEdit(row)} data-testid={`edit-${row.automation_id}`}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(row.automation_id)} data-testid={`delete-${row.automation_id}`}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="automation-form">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardContent className="p-5 space-y-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                {editingId ? 'Edit automation' : 'New automation'}
              </h3>

              <div>
                <label className="text-xs font-medium">Name</label>
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Monthly Reconciliation Folder" data-testid="form-name" />
              </div>
              <div>
                <label className="text-xs font-medium">Description (optional)</label>
                <Textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="What does this do?" data-testid="form-description" />
              </div>

              {/* Trigger */}
              <div className="border border-border rounded p-3 space-y-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Trigger</p>
                <Select value={draft.trigger_type} onValueChange={(v) => setDraft({ ...draft, trigger_type: v, trigger_config: {} })}>
                  <SelectTrigger data-testid="form-trigger-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TRIGGER_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>

                {draft.trigger_type === 'on_contact_create' && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px]">Contact type</label>
                      <Select value={draft.trigger_config.contact_type || 'all'} onValueChange={(v) => setDraft({ ...draft, trigger_config: { ...draft.trigger_config, contact_type: v } })}>
                        <SelectTrigger data-testid="form-contact-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any</SelectItem>
                          <SelectItem value="client">Client</SelectItem>
                          <SelectItem value="vendor">Vendor</SelectItem>
                          <SelectItem value="prospect">Prospect</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[11px]">Industry filter (optional)</label>
                      <Input placeholder="e.g. real_estate" value={draft.trigger_config.industry || ''} onChange={(e) => setDraft({ ...draft, trigger_config: { ...draft.trigger_config, industry: e.target.value } })} data-testid="form-industry" />
                    </div>
                  </div>
                )}
                {draft.trigger_type === 'monthly' && (
                  <div>
                    <label className="text-[11px]">Day of month (1–28)</label>
                    <Input type="number" min="1" max="28" value={draft.trigger_config.day_of_month || 1} onChange={(e) => setDraft({ ...draft, trigger_config: { ...draft.trigger_config, day_of_month: parseInt(e.target.value) || 1 } })} data-testid="form-day-of-month" />
                  </div>
                )}
                {draft.trigger_type === 'yearly' && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px]">Month (1–12)</label>
                      <Input type="number" min="1" max="12" value={draft.trigger_config.month || 1} onChange={(e) => setDraft({ ...draft, trigger_config: { ...draft.trigger_config, month: parseInt(e.target.value) || 1 } })} data-testid="form-month" />
                    </div>
                    <div>
                      <label className="text-[11px]">Day (1–28)</label>
                      <Input type="number" min="1" max="28" value={draft.trigger_config.day || 1} onChange={(e) => setDraft({ ...draft, trigger_config: { ...draft.trigger_config, day: parseInt(e.target.value) || 1 } })} data-testid="form-day" />
                    </div>
                  </div>
                )}
              </div>

              {/* Action */}
              <div className="border border-border rounded p-3 space-y-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Action</p>
                <Select value={draft.action_type} onValueChange={(v) => setDraft({ ...draft, action_type: v })}>
                  <SelectTrigger data-testid="form-action-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ACTION_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>

                {draft.action_type === 'apply_template' && (
                  <div>
                    <label className="text-[11px]">Template</label>
                    <Select value={draft.template_id} onValueChange={(v) => setDraft({ ...draft, template_id: v })}>
                      <SelectTrigger data-testid="form-template"><SelectValue placeholder="Pick a template…" /></SelectTrigger>
                      <SelectContent>
                        {templates.map(t => <SelectItem key={t.template_id} value={t.template_id}>{t.name} <span className="text-[10px] text-muted-foreground ml-1">· {t.space}</span></SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {draft.action_type === 'create_folder' && (
                  <div>
                    <label className="text-[11px]">Folder name (supports <code>{'{Year}'}</code>, <code>{'{Month}'}</code>, <code>{'{Quarter}'}</code>, <code>{'{Date}'}</code>)</label>
                    <Input value={draft.folder_name} onChange={(e) => setDraft({ ...draft, folder_name: e.target.value })} placeholder="e.g. {Year}-{Month} Reconciliation" data-testid="form-folder-name" />
                  </div>
                )}
                {draft.action_type === 'rollover' && (
                  <div>
                    <label className="text-[11px]">Source folder to roll forward</label>
                    <Select value={draft.rollover_source_folder_id} onValueChange={(v) => setDraft({ ...draft, rollover_source_folder_id: v })}>
                      <SelectTrigger data-testid="form-rollover-source"><SelectValue placeholder="Pick a folder…" /></SelectTrigger>
                      <SelectContent>
                        {folders.filter(f => !f.is_deleted && !f.is_archived).slice(0, 200).map(f => <SelectItem key={f.folder_id} value={f.folder_id}>{f.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {draft.action_type !== 'rollover' && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px]">Target space</label>
                      <Select value={draft.target_space} onValueChange={(v) => setDraft({ ...draft, target_space: v })}>
                        <SelectTrigger data-testid="form-space"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="firm">Organization</SelectItem>
                          <SelectItem value="personal">My Files</SelectItem>
                          <SelectItem value="client">Clients</SelectItem>
                          <SelectItem value="vendor">Vendors</SelectItem>
                          <SelectItem value="prospect">Prospects</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[11px]">Parent folder (optional)</label>
                      <Select value={draft.target_parent_folder_id || '__root__'} onValueChange={(v) => setDraft({ ...draft, target_parent_folder_id: v === '__root__' ? '' : v })}>
                        <SelectTrigger data-testid="form-parent"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__root__">— Space root —</SelectItem>
                          {folders.filter(f => !f.is_deleted && f.space === draft.target_space).slice(0, 200).map(f => <SelectItem key={f.folder_id} value={f.folder_id}>{f.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 pt-1">
                <Switch checked={draft.enabled} onCheckedChange={(v) => setDraft({ ...draft, enabled: v })} data-testid="form-enabled" />
                <span className="text-xs">{draft.enabled ? 'Enabled' : 'Disabled'}</span>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setShowForm(false)} data-testid="form-cancel">Cancel</Button>
                <Button onClick={save} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="form-save">
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
